import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";
import { useGet } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { c$ } from "@/lib/tips";
import { pct, dateTime } from "@/lib/fmt";
import { Button } from "@/components/ui/button";
import { ListenButton } from "@/components/app/ListenButton";

const Stat = ({ l, v, t }) => <div className="rounded-lg bg-[#FBF9F5] p-3" data-testid={t}><div className="text-[11px] uppercase tracking-wider text-stone-500">{l}</div><div className="font-display text-xl num">{v}</div></div>;

export function MyInsights() {
  const { data } = useGet("/me/tips/insights?days=90");
  const coach = useGet("/me/coaching");
  const [busy, setBusy] = useState(false);
  const auto = useRef(false);
  const ask = async () => {
    setBusy(true);
    try { await api.post("/me/coaching/generate", {}); coach.refetch(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const cur = coach.data?.current;
  useEffect(() => { if (coach.data?.outdated && !auto.current) { auto.current = true; ask(); } }, [coach.data?.outdated]); // eslint-disable-line react-hooks/exhaustive-deps
  const ans = cur?.answer;
  if (!data) return null;
  const s = data.sales;
  return (
    <div className="card-surface p-5 mb-3 space-y-3" data-testid="my-insights">
      <div className="label-eyebrow">Last 90 days · {data.role || "Your role"}</div>
      <div className="grid grid-cols-2 gap-2">
        <Stat l="Tips per hour" v={data.tips_per_hour_cents != null ? c$(data.tips_per_hour_cents) : "—"} t="ins-tph" />
        <Stat l="Hours" v={data.hours} t="ins-hours" />
        <Stat l="Avg tip-out share" v={data.avg_tipout_share_pct != null ? pct(data.avg_tipout_share_pct) : "—"} t="ins-share" />
        <Stat l="My avg check" v={s.avg_check_cents ? c$(s.avg_check_cents) : "—"} t="ins-avg-check" />
      </div>
      {!!s.categories.length && <div className="text-sm space-y-1" data-testid="ins-mix">{s.categories.slice(0, 5).map((c) => (
        <div key={c.name} className="flex justify-between"><span>{c.name}</span><span className="num text-stone-600">{`${pct(s.my_mix_pct[c.name], 0)} of my sales · team ${pct(s.restaurant_mix_pct[c.name], 0)}`}</span></div>))}</div>}
      <Button variant="outline" className="w-full h-11" onClick={ask} disabled={busy} data-testid="ins-coach-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}{cur ? "Refresh my coaching" : "Get coaching tips from 86'd AI"}</Button>
      {coach.data?.outdated && <div className="rounded-lg bg-amber-50 text-amber-900 text-xs p-2" data-testid="ins-coach-outdated">Your records changed since this coaching was written (for example, a correction). {busy ? "Updating it now…" : "Tap Refresh to update it."}</div>}
      {cur && <div className="text-[11px] text-stone-500" data-testid="ins-coach-meta">Generated {dateTime(cur.generated_at)} · period {cur.period.from} to {cur.period.to} · based on {cur.source_versions.sales_records} sales record(s) and {cur.source_versions.tip_reports} tip-out report(s){coach.data.versions > 1 ? ` · ${coach.data.versions} versions kept` : ""}</div>}
      {!!cur?.data_limits?.length && <div className="text-xs text-stone-600" data-testid="ins-coach-limits">Data limits: {cur.data_limits.join("; ")}</div>}
      {ans && <div className="text-sm whitespace-pre-wrap rounded-lg bg-[#F3EFE6] p-3" data-testid="ins-coach-answer">{ans}</div>}
      {ans && <ListenButton text={ans} label="Listen to coaching" testid="ins-coach-listen" />}
    </div>
  );
}
